import React, { useState, useCallback, useRef } from 'react';
import './SongLibrary.css';
import { deleteSongRemote, exportLibraryFile, importLibraryRemote } from '../utils/songLibrary';

/**
 * Song library drawer.
 *
 * Props:
 *   open               — boolean, controls visibility
 *   onClose            — () => void
 *   onLoad             — (song) => void — called when user selects a song
 *   library            — current library array (may have been updated from parent)
 *   onLibraryChange    — (newLibrary) => void — called after deletions
 */
function SongLibrary({ open, onClose, onLoad, library, onLibraryChange }) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const importRef = useRef();

  const handleDelete = useCallback(async (id) => {
    if (confirmDelete === id) {
      try {
        const updated = await deleteSongRemote(id);
        onLibraryChange(updated);
        setConfirmDelete(null);
      } catch {
        setConfirmDelete(null);
      }
    } else {
      setConfirmDelete(id);
    }
  }, [confirmDelete, onLibraryChange]);

  const handleImport = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data)) return;
        const merged = await importLibraryRemote(data);
        onLibraryChange(merged);
      } catch {}
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [onLibraryChange]);

  if (!open) return null;

  return (
    <>
      <div className="song-library-overlay" onClick={onClose} />
      <aside className="song-library">
        <div className="song-library__header">
          <h2 className="song-library__title">📚 Song Library</h2>
          <button className="song-library__close" onClick={onClose} aria-label="Close library">✕</button>
        </div>

        {library.length === 0 ? (
          <div className="song-library__empty">
            <p>No saved songs yet.</p>
            <p>Use <strong>Save</strong> in the toolbar to save the current song.</p>
          </div>
        ) : (
          <ul className="song-library__list">
            {library.map(song => (
              <li key={song.id} className="song-library__item">
                <div className="song-library__item-info">
                  <span className="song-library__item-title">{song.title}</span>
                  <span className="song-library__item-meta">
                    {song.songKey ? `Key: ${song.songKey}` : ''}
                    {song.savedAt ? ` · ${new Date(song.savedAt).toLocaleDateString()}` : ''}
                  </span>
                </div>
                <div className="song-library__item-actions">
                  <button
                    className="song-library__btn song-library__btn--load"
                    onClick={() => { onLoad(song); onClose(); }}
                  >
                    Load
                  </button>
                  <button
                    className={`song-library__btn song-library__btn--delete${confirmDelete === song.id ? ' confirming' : ''}`}
                    onClick={() => handleDelete(song.id)}
                    title={confirmDelete === song.id ? 'Click again to confirm deletion' : 'Delete'}
                  >
                    {confirmDelete === song.id ? 'Confirm?' : '✕'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="song-library__footer">
          <button
            className="song-library__btn song-library__btn--secondary"
            onClick={() => exportLibraryFile(library)}
            disabled={library.length === 0}
            title="Export all saved songs as a JSON backup file"
          >
            Export
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImport}
          />
          <button
            className="song-library__btn song-library__btn--secondary"
            onClick={() => importRef.current.click()}
            title="Import songs from a previously exported JSON file"
          >
            Import
          </button>
        </div>
      </aside>
    </>
  );
}

export default SongLibrary;
